import { useMemo, useState } from "react"
import { ChevronRight } from "lucide-react"

import { CohortAvatar } from "components/mentorship/CohortExperience"
import { Button } from "components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "components/ui/sheet"
import { MentorPairingsManager, type MentorPairingUpdate } from "./MentorPairingsManager"
import type { Person } from "./types"

type MentorPairingsCardProps = {
  display?: "responsive" | "mobile" | "desktop"
  roster: Person[]
  mentorLabelSingular?: string
  mentorLabelPlural?: string
  memberLabelSingular?: string
  memberLabelPlural?: string
  onSavePairings: (updates: MentorPairingUpdate[]) => Promise<void>
  onSelectPerson?: (person: Person) => void
}

type PairingRow = {
  mentor: Person
  mentees: Person[]
}

function PairingRows({
  pairings,
  unpaired,
  mentorLabelSingular,
  memberLabelSingular,
  memberLabelPlural,
  onSelectPerson,
}: {
  pairings: PairingRow[]
  unpaired: Person[]
  mentorLabelSingular: string
  memberLabelSingular: string
  memberLabelPlural: string
  onSelectPerson?: (person: Person) => void
}) {
  return (
    <div className="cohort-bounded-list cohort-bounded-list--compact">
      {pairings.map(({ mentor, mentees }) => (
        <button key={mentor.id} type="button" onClick={() => onSelectPerson?.(mentor)} className="cohort-row-action flex min-h-14 w-full items-center gap-3 border-b border-[var(--cohort-rule-soft)] py-3 text-left">
          <CohortAvatar person={mentor} size="pairingRow" tone="mentor" />
          <span className="min-w-0 flex-1">
            <span className="block break-words text-base font-medium">{mentor.name}</span>
            <span className="mt-0.5 block text-sm text-[var(--cohort-ink-muted)]">{mentees.length} {mentees.length === 1 ? memberLabelSingular : memberLabelPlural}</span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--cohort-ink-subtle)]" aria-hidden="true" />
        </button>
      ))}
      {unpaired.length > 0 ? (
        <div className="flex min-h-14 items-center gap-3 py-3">
          <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[var(--cohort-muted-fill)] text-[13px] font-semibold text-[var(--cohort-muted-ink)]">—</span>
          <span className="min-w-0 flex-1">
            <span className="block break-words text-base font-medium">{unpaired.map((person) => person.name).join(", ")}</span>
            <span className="mt-0.5 block text-sm text-[var(--cohort-clay)]">No {mentorLabelSingular.toLowerCase()} assigned</span>
          </span>
        </div>
      ) : null}
      {pairings.length === 0 && unpaired.length === 0 ? <p className="py-4 text-sm text-[var(--cohort-ink-muted)]">No {mentorLabelSingular.toLowerCase()} relationships are available yet.</p> : null}
    </div>
  )
}

export function MentorPairingsCard({
  display = "responsive",
  roster,
  mentorLabelSingular = "Mentor",
  mentorLabelPlural = "Mentors",
  memberLabelSingular = "member",
  memberLabelPlural = "members",
  onSavePairings,
  onSelectPerson,
}: MentorPairingsCardProps) {
  const [open, setOpen] = useState(false)
  const [managerOpen, setManagerOpen] = useState(false)
  const showMobile = display !== "desktop"
  const showDesktop = display !== "mobile"
  const { mentors, pairings, unpaired } = useMemo(() => {
    const nextMentors = roster.filter((person) => person.roleKey === "mentor")
    const mentees = roster.filter((person) => person.roleKey === "mentee" || !person.roleKey)
    const mentorIds = new Set(nextMentors.map((mentor) => mentor.id))
    const nextPairings = nextMentors.map((mentor) => ({
      mentor,
      mentees: mentees.filter((person) => person.mentorId === mentor.id),
    }))
    return {
      mentors: nextMentors,
      pairings: nextPairings,
      unpaired: mentees.filter((person) => !person.mentorId || !mentorIds.has(person.mentorId)),
    }
  }, [roster])

  const closeThen = (action?: () => void) => {
    setOpen(false)
    if (action) window.setTimeout(action, 0)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {showMobile ? (
        <SheetTrigger asChild>
          <button type="button" aria-expanded={open} aria-haspopup="dialog" className="cohort-relationship-mobile-row flex min-h-14 w-full items-center gap-[11px] border-b border-[var(--cohort-rule)] bg-[var(--cohort-rest)] px-5 text-left sm:hidden">
            <span className="flex w-[51px] shrink-0 -space-x-[9px]">
              {mentors.slice(0, 2).map((mentor) => <CohortAvatar key={mentor.id} person={mentor} size="pairing" tone="mentor" />)}
              {mentors.length === 0 ? <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border border-dashed border-[var(--cohort-rule-strong)] text-xs">—</span> : null}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs text-[var(--cohort-ink-muted)]">{mentorLabelSingular} pairings</span>
              <span className="block break-words text-[15px] font-medium leading-5">{mentors.length} {(mentors.length === 1 ? mentorLabelSingular : mentorLabelPlural).toLowerCase()} · <span className={unpaired.length ? "text-[var(--cohort-clay)]" : ""}>{unpaired.length} {(unpaired.length === 1 ? memberLabelSingular : memberLabelPlural).toLowerCase()} unpaired</span></span>
            </span>
            <ChevronRight className="h-[17px] w-[17px] shrink-0 text-[var(--cohort-ink-subtle)]" aria-hidden="true" />
          </button>
        </SheetTrigger>
      ) : null}

      {showDesktop ? (
        <section className="hidden rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-5 sm:block" aria-labelledby="mentor-pairings-heading">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="mentor-pairings-heading" className="text-xl">{mentorLabelSingular} pairings</h2>
            {unpaired.length > 0 ? <span className="text-sm text-[var(--cohort-clay)]">{unpaired.length} unpaired</span> : null}
          </div>
          <PairingRows pairings={pairings} unpaired={unpaired} mentorLabelSingular={mentorLabelSingular} memberLabelSingular={memberLabelSingular.toLowerCase()} memberLabelPlural={memberLabelPlural.toLowerCase()} onSelectPerson={onSelectPerson} />
          <Button type="button" variant="outline" onClick={() => setManagerOpen(true)} className="cohort-secondary-button mt-3 w-full">Manage pairings</Button>
        </section>
      ) : null}

      <SheetContent
        side="bottom"
        overlayClassName="cohort-relationship-overlay"
        style={{ pointerEvents: "auto" }}
        className="cohort-experience pointer-events-auto max-h-[calc(100dvh-3rem)] gap-0 overflow-y-auto rounded-t-[22px] border-0 bg-[var(--cohort-rest)] p-0 text-[var(--cohort-ink)] shadow-[0_-22px_50px_-22px_rgba(23,26,34,0.5)] [&>button]:hidden sm:hidden"
      >
        <SheetDescription className="sr-only">Review {mentorLabelSingular.toLowerCase()} assignments for this cohort.</SheetDescription>
        <div className="flex justify-center pb-0.5 pt-2.5" aria-hidden="true"><span className="h-1 w-11 rounded-full bg-[var(--cohort-rule-strong)]" /></div>
        <div className="flex items-baseline justify-between gap-3 px-5 pb-3.5 pt-3">
          <SheetTitle className="text-[21px] font-normal tracking-[-0.01em]">{mentorLabelSingular} pairings</SheetTitle>
          {unpaired.length > 0 ? <span className="text-sm text-[var(--cohort-clay)]">{unpaired.length} unpaired</span> : null}
        </div>
        <div className="border-t border-[var(--cohort-rule-soft)] px-5">
          <PairingRows
            pairings={pairings}
            unpaired={unpaired}
            mentorLabelSingular={mentorLabelSingular}
            memberLabelSingular={memberLabelSingular.toLowerCase()}
            memberLabelPlural={memberLabelPlural.toLowerCase()}
            onSelectPerson={(person) => closeThen(() => onSelectPerson?.(person))}
          />
        </div>
        <div className="border-t border-[var(--cohort-rule-soft)] bg-[var(--cohort-paper)] px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3.5">
          <Button type="button" variant="outline" onClick={() => closeThen(() => setManagerOpen(true))} className="cohort-secondary-button min-h-[52px] w-full text-[17px]">Manage pairings</Button>
        </div>
      </SheetContent>
      {managerOpen ? (
        <MentorPairingsManager
          mode={display === "mobile" ? "mobile" : "desktop"}
          open
          roster={roster}
          mentorLabelSingular={mentorLabelSingular}
          mentorLabelPlural={mentorLabelPlural}
          memberLabelSingular={memberLabelSingular}
          memberLabelPlural={memberLabelPlural}
          onOpenChange={(nextOpen) => setManagerOpen(nextOpen)}
          onSave={onSavePairings}
        />
      ) : null}
    </Sheet>
  )
}
